import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, compose, withApollo } from 'react-apollo';
import _ from 'lodash';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import TablePagination from '@material-ui/core/TablePagination';
import Typography from '@material-ui/core/Typography';
import Search from '@material-ui/icons/Search';
import { Grid } from '@material-ui/core';
import { InlineDatePicker, MuiPickersUtilsProvider } from 'material-ui-pickers';
import DateFnsUtils from '@date-io/date-fns';
import { CSVLink } from 'react-csv';
import { FaFileExport } from 'react-icons/fa';

import { getManualNotificationsByAdmin } from './queries';
import { Dialog } from '../../../components';
import CustomInput from '../../../components/CustomInput/CustomInput';
import styles from './styles';
import colors from '../../../constants/colors';

const CustomTableCell = withStyles(theme => ({
  head: {
    backgroundColor: colors.parimary,
    color: theme.palette.common.white,
  },
  body: {
    fontSize: 14,
  },
}))(TableCell);

class TableNotificationHistory extends Component {
  state = {
    page: 1,
    limit: 15,
    fromDate: null,
    toDate: null,
    username: '',
    title: '',
    exportData: [],
    exporting: false,
    detailOpen: false,
    selectedNotification: null,
  }

  resetFilters = () => {
    this.setState({
      fromDate: null,
      toDate: null,
      username: '',
      title: '',
      page: 1,
    });
  }

  handleChangePage = (event, page) => {
    this.setState({ page: page + 1 }, () => {
      this.refetchData();
    });
  }

  handleChangeRowsPerPage = (event) => {
    this.setState({ limit: event.target.value, page: 1 }, () => {
      this.refetchData();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { page, limit, fromDate, toDate, username, title } = this.state;
    if (
      prevState.page !== page
      || prevState.limit !== limit
      || prevState.fromDate !== fromDate
      || prevState.toDate !== toDate
      || prevState.username !== username
      || prevState.title !== title
    ) {
      this.refetchData();
    }
  }

  refetchData = () => {
    const { refetch } = this.props;
    if (refetch) {
      const { page, limit } = this.state;
      refetch({
        input: {
          ...this.buildFiltersInput(),
          page: page || 1,
          limit: limit || 15,
        },
      });
    }
  }

  buildFiltersInput = () => {
    const { fromDate, toDate, username, title } = this.state;
    return {
      fromDate: fromDate ? moment(fromDate).format('YYYY-MM-DD') : null,
      toDate: toDate ? moment(toDate).format('YYYY-MM-DD') : null,
      username: username || null,
      title: title || null,
    };
  }

  getRecipientType = (notification) => {
    if (notification.user) return 'Merchant';
    if (notification.consumer) return 'Consumer';
    return '-';
  }

  getRecipientUsername = (notification) => {
    if (notification.user) return notification.user.username || '-';
    if (notification.consumer) return notification.consumer.username || '-';
    return '-';
  }

  handleOpenDetail = (notification) => {
    this.setState({ detailOpen: true, selectedNotification: notification });
  }

  handleCloseDetail = () => {
    this.setState({ detailOpen: false, selectedNotification: null });
  }

  renderDetailContent = () => {
    const { selectedNotification } = this.state;
    if (!selectedNotification) return null;

    const date = selectedNotification.createdAt
      ? moment(Number(selectedNotification.createdAt))
      : null;

    return (
      <div style={{ minWidth: 320 }}>
        <Typography variant="body2" gutterBottom>
          <strong>Date:</strong> {date ? date.format('MM/DD/YYYY HH:mm') : '-'}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Recipient:</strong> {this.getRecipientType(selectedNotification)}
          {' — '}
          {this.getRecipientUsername(selectedNotification)}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Title:</strong> {selectedNotification.title || '-'}
        </Typography>
        <Typography variant="body2" gutterBottom>
          <strong>Description:</strong> {selectedNotification.description || '-'}
        </Typography>
        <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
          Mensaje en Español
        </Typography>
        <Typography
          variant="body2"
          style={{ whiteSpace: 'pre-wrap', marginBottom: 16 }}
        >
          {selectedNotification.messageInSpanish || '-'}
        </Typography>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Mensaje en Inglés
        </Typography>
        <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
          {selectedNotification.messageInEnglish || '-'}
        </Typography>
      </div>
    );
  }

  prepareDataForCSV = (notifications = []) => {
    let data = notifications.map((notification) => {
      const date = notification.createdAt ? moment(Number(notification.createdAt)) : null;
      return {
        Date: date ? date.format('MM/DD/YYYY') : '',
        Time: date ? date.format('HH:mm') : '',
        'Recipient Type': this.getRecipientType(notification),
        Username: this.getRecipientUsername(notification),
        Title: notification.title || '',
        Description: notification.description || '',
        'Message (ES)': notification.messageInSpanish || '',
        'Message (EN)': notification.messageInEnglish || '',
      };
    });

    data = _.orderBy(data, (obj) => {
      if (obj.Date && obj.Time) {
        return moment(`${obj.Date} ${obj.Time}`, 'MM/DD/YYYY HH:mm').valueOf();
      }
      return 0;
    }, 'desc');

    return data;
  }

  onExportToCSV = () => {
    const { client } = this.props;
    const { limit } = this.state;
    if (!client) return;

    this.setState({ exporting: true }, async () => {
      try {
        const baseInput = {
          ...this.buildFiltersInput(),
          page: 1,
          limit: limit || 15,
        };

        const firstResult = await client.query({
          query: getManualNotificationsByAdmin,
          variables: { input: baseInput },
          fetchPolicy: 'no-cache',
        });
        const firstPayload = firstResult && firstResult.data
          ? firstResult.data.getManualNotificationsByAdmin
          : null;

        if (!firstPayload || !firstPayload.count_rows) {
          this.setState({ exporting: false, exportData: [] });
          return;
        }

        let allNotifications = (firstPayload.notifications || []).slice();
        const totalPage = firstPayload.total_page || 1;

        for (let currentPage = 2; currentPage <= totalPage; currentPage += 1) {
          const pageResult = await client.query({
            query: getManualNotificationsByAdmin,
            variables: { input: { ...baseInput, page: currentPage } },
            fetchPolicy: 'no-cache',
          });
          const pagePayload = pageResult && pageResult.data
            ? pageResult.data.getManualNotificationsByAdmin
            : null;
          if (pagePayload && pagePayload.notifications && pagePayload.notifications.length) {
            allNotifications = allNotifications.concat(pagePayload.notifications);
          }
        }

        const exportData = this.prepareDataForCSV(allNotifications);
        this.setState({ exportData, exporting: false }, () => {
          if (this.csvLinkRef) {
            this.csvLinkRef.link.click();
          }
        });
      } catch (error) {
        this.setState({ exporting: false });
      }
    });
  }

  renderExportButtonLabel = () => {
    const { classes } = this.props;
    const { exporting } = this.state;
    return (
      <Typography component="p" className={classes.text}>
        {exporting ? 'Exporting...' : 'Export'}
      </Typography>
    );
  }

  renderToolBar = () => {
    const { classes } = this.props;
    const {
      fromDate, toDate, username, title, exportData, exporting,
    } = this.state;
    const { getManualNotificationsByAdmin: data } = this.props;
    const countRows = (data && data.count_rows) || 0;

    return (
      <>
        <div className={classes.searchWrapper}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <CustomInput
              formControlProps={{
                className: `${classes.margin} ${classes.search}`,
              }}
              inputProps={{
                value: username,
                onChange: (e) => this.setState({ username: e.target.value, page: 1 }),
                placeholder: 'Username',
                inputProps: {
                  'aria-label': 'Username',
                },
              }}
            />
            <Button disabled>
              <Search />
            </Button>
            <CustomInput
              formControlProps={{
                className: `${classes.margin} ${classes.search}`,
              }}
              inputProps={{
                value: title,
                onChange: (e) => this.setState({ title: e.target.value, page: 1 }),
                placeholder: 'Title',
                inputProps: {
                  'aria-label': 'Title',
                },
              }}
            />
            <Button disabled>
              <Search />
            </Button>
            <Button variant="contained" color="primary" className={classes.button} onClick={this.onExportToCSV} disabled={exporting}>
              {this.renderExportButtonLabel()}
              <FaFileExport color="#ffffff" size={16} />
            </Button>
            <CSVLink
              data={exportData}
              filename={`notification-history-${moment().format('YYYY-MM-DD-HHmmss')}.csv`}
              style={{ display: 'none' }}
              ref={(r) => { this.csvLinkRef = r; }}
            >
              Export
            </CSVLink>
          </div>
          <Typography component="p" style={{ fontSize: '14px', alignSelf: 'center' }}>
            Rows: {countRows}
          </Typography>
        </div>
        <div>
          <Typography variant="body1" className={classes.filterLabel}>Date Filter</Typography>
          <Grid container>
            <Grid container item xs={12} md={8}>
              <Grid item xs={12} sm={6}>
                <InlineDatePicker
                  label="From"
                  id="date-from"
                  onChange={(date) => this.setState({ fromDate: date, page: 1 })}
                  format="yyyy-MM-dd"
                  value={fromDate}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InlineDatePicker
                  label="To"
                  id="date-to"
                  onChange={(date) => this.setState({ toDate: date, page: 1 })}
                  format="yyyy-MM-dd"
                  value={toDate}
                />
              </Grid>
            </Grid>
            <Grid container item xs={12} md={4}>
              <Button variant="contained" color="primary" className={classes.button} onClick={this.resetFilters}>
                Reset
              </Button>
            </Grid>
          </Grid>
        </div>
      </>
    );
  }

  render() {
    const { classes, getManualNotificationsByAdmin: data } = this.props;
    const notifications = (data && data.notifications) || [];
    const pagination = data || {};
    const { page, limit, detailOpen } = this.state;

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <div>
          <Dialog
            title="Notification Detail"
            listChildren={this.renderDetailContent()}
            open={detailOpen}
            onOk={this.handleCloseDetail}
          />
          {this.renderToolBar()}
          <Paper className={classes.root}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <CustomTableCell>Date</CustomTableCell>
                  <CustomTableCell align="right" className={classes.columnRow}>Time</CustomTableCell>
                  <CustomTableCell align="right" className={classes.columnRow}>Recipient</CustomTableCell>
                  <CustomTableCell align="right" className={classes.columnRow}>Username</CustomTableCell>
                  <CustomTableCell align="right" className={classes.columnRow}>Title</CustomTableCell>
                  <CustomTableCell align="right" className={classes.columnRow}>Description</CustomTableCell>
                  <CustomTableCell align="center" className={classes.columnRow}>Detail</CustomTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map((row) => {
                  const date = row.createdAt ? moment(Number(row.createdAt)) : null;
                  return (
                    <TableRow className={classes.row} key={row.id}>
                      <CustomTableCell component="th" scope="row">
                        {date ? date.format('MM/DD/YYYY') : '-'}
                      </CustomTableCell>
                      <CustomTableCell align="right" className={classes.colTable}>
                        {date ? date.format('HH:mm') : '-'}
                      </CustomTableCell>
                      <CustomTableCell align="right" className={classes.colTable}>
                        {this.getRecipientType(row)}
                      </CustomTableCell>
                      <CustomTableCell align="right" className={classes.colTable}>
                        {this.getRecipientUsername(row)}
                      </CustomTableCell>
                      <CustomTableCell align="right" className={classes.colTable}>
                        {row.title || '-'}
                      </CustomTableCell>
                      <CustomTableCell align="right" className={classes.colTable}>
                        {row.description || '-'}
                      </CustomTableCell>
                      <CustomTableCell align="center" className={classes.colTable}>
                        <Button
                          variant="contained"
                          color="primary"
                          className={classes.button}
                          onClick={() => this.handleOpenDetail(row)}
                        >
                          <Typography component="p" className={classes.text}>
                            Detail
                          </Typography>
                        </Button>
                      </CustomTableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[15, 25, 50]}
              component="div"
              count={pagination.count_rows || 0}
              rowsPerPage={limit}
              page={page - 1}
              backIconButtonProps={{
                'aria-label': 'Previous Page',
              }}
              nextIconButtonProps={{
                'aria-label': 'Next Page',
              }}
              onChangePage={this.handleChangePage}
              onChangeRowsPerPage={this.handleChangeRowsPerPage}
            />
          </Paper>
        </div>
      </MuiPickersUtilsProvider>
    );
  }
}

TableNotificationHistory.defaultProps = {
  classes: {},
  getManualNotificationsByAdmin: {},
};

TableNotificationHistory.propTypes = {
  classes: PropTypes.object,
  getManualNotificationsByAdmin: PropTypes.object,
  refetch: PropTypes.func,
  client: PropTypes.object,
};

const ManualNotificationsQuery = graphql(getManualNotificationsByAdmin, {
  options: () => ({
    variables: {
      input: {
        page: 1,
        limit: 15,
        fromDate: null,
        toDate: null,
        username: null,
        title: null,
      },
    },
    fetchPolicy: 'network-only',
  }),
  props: ({ data }) => ({
    loading: data.loading,
    getManualNotificationsByAdmin: data.getManualNotificationsByAdmin,
    refetch: data.refetch,
  }),
});

export default withStyles(styles)(compose(ManualNotificationsQuery, withApollo)(TableNotificationHistory));
